/**
 * Keeps asset view choices stable across browser sessions while isolating the UI
 * from stale or unavailable localStorage data.
 */
import { useCallback, useState } from 'react';
import {
  ASSET_VIEW_FIELDS,
  DEFAULT_PREFERENCES,
  type AssetViewPreferences,
} from 'src/components/assets/assetViewFields';

export const ASSET_VIEW_PREFERENCES_STORAGE_KEY = 'leela.assets.viewPreferences.v1';

interface PreferencesLoadResult {
  readonly preferences: AssetViewPreferences;
  readonly hasParseError: boolean;
}

function isRecord (value: unknown): value is Readonly<Record<string, unknown>> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function parsePreferences (raw: string | null): PreferencesLoadResult {
  if (raw === null) {
    return { preferences: DEFAULT_PREFERENCES, hasParseError: false };
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    const saved = isRecord(parsed) ? parsed : {};
    const savedFields = isRecord(saved.fields) ? saved.fields : {};
    const fields = { ...DEFAULT_PREFERENCES.fields };

    ASSET_VIEW_FIELDS.forEach(({ id }) => {
      const savedValue = savedFields[id];
      if (typeof savedValue === 'boolean') {
        fields[id] = savedValue;
      }
    });

    return {
      preferences: { fields },
      hasParseError: false,
    };
  } catch {
    return { preferences: DEFAULT_PREFERENCES, hasParseError: true };
  }
}

export function loadPreferences (raw: string | null): AssetViewPreferences {
  return parsePreferences(raw).preferences;
}

export function useAssetViewPreferences (): readonly [
  AssetViewPreferences,
  (next: AssetViewPreferences) => void
] {
  const [preferences, setPreferencesState] = useState<AssetViewPreferences>(() => {
    try {
      const result = parsePreferences(
        window.localStorage.getItem(ASSET_VIEW_PREFERENCES_STORAGE_KEY),
      );

      if (result.hasParseError) {
        console.warn('Unable to parse saved asset view preferences; using defaults.');
      }

      return result.preferences;
    } catch {
      console.warn('Unable to load saved asset view preferences; using defaults.');
      return DEFAULT_PREFERENCES;
    }
  });

  const setPreferences = useCallback((next: AssetViewPreferences): void => {
    setPreferencesState(next);

    try {
      window.localStorage.setItem(
        ASSET_VIEW_PREFERENCES_STORAGE_KEY,
        JSON.stringify({ fields: next.fields }),
      );
    } catch {
      console.warn('Unable to save asset view preferences.');
    }
  }, []);

  return [preferences, setPreferences] as const;
}
